/**
 * Pure, surface-agnostic formatting helpers for booking data.
 *
 * These live in helpers/ (not under Seller/ or Shopper/) because both the
 * seller admin console and the storefront slot picker render the same
 * underlying primitive — a UTC interval against a resource — and their
 * date/money formatting must not drift apart.
 *
 * Browser Intl only, no React or fetch: safe to import from anywhere.
 */
/**
 * Preserve the visitor's regional date/time conventions when they match the
 * translated UI language. A generic `en` catalog can therefore still render
 * 12-hour `en-US` or 24-hour `en-GB` times as appropriate.
 */
export function resolveBookingFormatLocale(uiLocale, browserLocales = null) {
    const requested = String(uiLocale || '').trim().replace(/_/g, '-');
    const detected = browserLocales || [
        ...(globalThis.navigator?.languages || []),
        globalThis.navigator?.language,
    ];
    const candidates = detected
        .filter(Boolean)
        .map(locale => String(locale).trim().replace(/_/g, '-'));
    if (requested.includes('-')) return requested;
    const requestedLanguage = requested.split('-')[0].toLowerCase();
    const regionalMatch = candidates.find(locale => (
        locale.split('-')[0].toLowerCase() === requestedLanguage
    ));
    return regionalMatch || requested || candidates[0] || undefined;
}

/**
 * Format a UTC date string in a given IANA timezone. Falls back to the
 * viewer's local zone if `timezone` is missing/invalid.
 */
export function formatDateTime(value, timezone, opts = {}) {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime())) return '';
    const { locale, ...formatOptions } = opts;
    try {
        return new Intl.DateTimeFormat(locale, {
            timeZone: timezone || undefined,
            dateStyle: formatOptions.dateStyle || 'medium',
            timeStyle: formatOptions.timeStyle || 'short',
            ...formatOptions,
        }).format(date);
    } catch (e) {
        return date.toLocaleString(locale);
    }
}

export function formatDateOnly(value, timezone, locale) {
    return formatDateTime(value, timezone, {
        dateStyle: 'medium',
        timeStyle: undefined,
        locale,
    });
}

export function formatTimeOnly(value, timezone, locale) {
    return formatDateTime(value, timezone, {
        dateStyle: undefined,
        timeStyle: 'short',
        locale,
    });
}

/**
 * A compact human range like "Jul 24, 2:00 – 3:00 PM" (same day) or
 * "Jul 24, 2:00 PM – Jul 26, 11:00 AM" (multi-day).
 */
export function formatInterval(startAt, endAt, timezone, locale) {
    if (!startAt) return '';
    const start = new Date(startAt);
    const end = endAt ? new Date(endAt) : null;
    const startStr = formatDateTime(start, timezone, { locale });
    if (!end || isNaN(end.getTime())) return startStr;

    const sameDay = formatDateOnly(start, timezone, locale)
        === formatDateOnly(end, timezone, locale);
    if (sameDay) {
        return `${startStr} – ${formatTimeOnly(end, timezone, locale)}`;
    }
    return `${startStr} – ${formatDateTime(end, timezone, { locale })}`;
}

/**
 * Money is stored as integer cents on booking pricing snapshots.
 */
export function formatCents(cents, currency = 'USD', locale) {
    if (cents === null || cents === undefined) return '';
    try {
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency || 'USD',
        }).format(cents / 100);
    } catch (e) {
        return `$${(cents / 100).toFixed(2)}`;
    }
}

export function formatDurationMinutes(minutes) {
    if (!minutes && minutes !== 0) return '';
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs && mins) return `${hrs}h ${mins}m`;
    if (hrs) return `${hrs}h`;
    return `${mins}m`;
}

/**
 * The viewer's best-guess IANA timezone.
 */
export function guessTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch (e) {
        return 'UTC';
    }
}

/**
 * `YYYY-MM-DD` for a date, as seen in a given timezone. Booking date keys
 * (daterange nights, day grouping) must be computed in the display zone or
 * slots land on the wrong day either side of midnight.
 */
export function dateKeyInZone(value, timezone) {
    const date = new Date(value);
    if (isNaN(date.getTime())) return '';
    try {
        // en-CA gives ISO-ordered YYYY-MM-DD
        return new Intl.DateTimeFormat('en-CA', {
            timeZone: timezone || undefined,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).format(date);
    } catch (e) {
        const pad = n => String(n).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    }
}
